import { useCallback } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import { FreshnessNote } from "@/components/FreshnessNote";
import { PopularCardsSection } from "@/components/PopularCardsSection";
import { Link, useNavigate } from "@/lib/router-compat";
import { getSiteOrigin } from "@/lib/ssr-safe";
import { reviewDateISO } from "@/lib/currentYear";

export default function UeberUns() {
  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (page: string) => {
      if (page === "home") navigate("/");
    },
    [navigate],
  );

  const url = `${getSiteOrigin()}/ueber-uns`;
  const title = "Über uns: Wer hinter Kreditkarten.de steht";
  const description =
    "Kreditkarten.de ist ein unabhängiger Kreditkartenvergleich für Deutschland. Wer wir sind, wie wir arbeiten, wie wir finanziert sind und wie Sie uns erreichen.";

  return (
    <div className="min-h-screen bg-background">
      <SEOHelmet
        title={`${title} | Kreditkarten.de`}
        description={description}
        url={url}
        type="website"
        modifiedTime={reviewDateISO()}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: title,
          description,
          url,
          dateModified: reviewDateISO(),
          mainEntity: {
            "@type": "Organization",
            name: "Kreditkarten.de",
            url: "https://www.kreditkarten.de",
            parentOrganization: {
              "@type": "Organization",
              name: "Nettbureau AS",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Kjølberggata 31",
                postalCode: "0653",
                addressLocality: "Oslo",
                addressCountry: "NO",
              },
            },
            email: "kontakt@kreditkarten.de",
          },
        }}
      />
      <Header onNavigate={handleNavigate} currentPage="ueber-uns" />

      <main className="container mx-auto px-4 py-10 max-w-3xl">
        <nav className="text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:text-primary">
            Startseite
          </Link>{" "}
          <span aria-hidden="true">›</span> Über uns
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold mb-3">Über Kreditkarten.de</h1>
        <p className="text-lg text-muted-foreground mb-2">
          Wir vergleichen Kreditkarten für den deutschen Markt – mit nachprüfbaren Zahlen statt
          Sternebewertungen.
        </p>
        <FreshnessNote className="mb-10" label="Zuletzt geprüft" />

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Was wir machen</h2>
          <p className="text-muted-foreground mb-3">
            Kreditkarten unterscheiden sich in Deutschland vor allem in drei Punkten:
            Jahresgebühr, Kosten im Ausland und der Frage, ob es überhaupt eine echte
            Kreditkarte ist. Genau diese Punkte stellen wir bei jeder Karte gleich dar, damit
            ein Vergleich möglich ist.
          </p>
          <p className="text-muted-foreground">
            Wir geben keine Empfehlung ab, welche Karte „die beste“ ist. Wir zeigen, welche
            Karte zu welchem Nutzungsverhalten passt – und was sie in diesem Fall kostet.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Über Nettbureau</h2>
          <p className="text-muted-foreground mb-3">
            Kreditkarten.de ist ein Service der <strong className="text-foreground">Nettbureau AS</strong>,
            einem norwegischen Unternehmen mit mehr als 10 Jahren Erfahrung in der Entwicklung
            digitaler Vergleichsdienste, die Verbraucher und Anbieter zu fairen Konditionen
            zusammenbringen.
          </p>
          <p className="text-muted-foreground mb-3">
            Nettbureau AS ist in sieben Ländern aktiv: Norwegen, Schweden, Dänemark, Spanien,
            Italien, Frankreich und Deutschland. Mehrere der Vergleichsdienste des Unternehmens
            gehören in den nordischen Ländern zu den Marktführern. Seit 2009 hat Nettbureau über
            eine Million Anfragen von Privatpersonen und Unternehmen erhalten.
          </p>
          <address className="not-italic text-muted-foreground mb-3">
            Nettbureau AS
            <br />
            Kjølberggata 31
            <br />
            0653 Oslo, Norwegen
          </address>
          <p className="text-muted-foreground">
            Verantwortlich im Sinne des Presserechts und alle Unternehmensangaben finden Sie im{" "}
            <Link to="/impressum" className="text-primary underline underline-offset-4">
              Impressum
            </Link>
            .
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Unsere Grundsätze</h2>
          <ul className="space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Nachprüfbar vor vollständig.</strong> Lieber
              ein leeres Feld als eine geschätzte Zahl.
            </li>
            <li>
              <strong className="text-foreground">Keine Pseudo-Bewertungen.</strong> Keine
              Sterne, keine Punktzahlen, keine „Testsieger“, die wir nicht getestet haben.
            </li>
            <li>
              <strong className="text-foreground">Werbung ist erkennbar.</strong> Bezahlte
              Platzierungen tragen immer den Hinweis „Anzeige“.
            </li>
            <li>
              <strong className="text-foreground">Kartentyp zuerst.</strong> Debit-, Charge-,
              Prepaid- und Revolving-Karten funktionieren unterschiedlich – wir vermischen sie
              nicht.
            </li>
            <li>
              <strong className="text-foreground">Monatliche Prüfung.</strong> Jede Vergleichs-
              und Ratgeberseite wird monatlich überprüft, Kartendaten laufend aktualisiert.
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Wie wir finanziert sind</h2>
          <p className="text-muted-foreground">
            Unser Vergleich ist kostenlos. Beantragen Sie eine Karte über einen Link auf unserer
            Seite, erhalten wir häufig eine Provision vom Anbieter – für Sie ohne Aufpreis. Wie
            sich das auf Reihenfolge und Darstellung auswirkt (und wie nicht), erklären wir in
            unserer{" "}
            <Link to="/methodik" className="text-primary underline underline-offset-4">
              Methodik
            </Link>
            .
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Kontakt und Korrekturen</h2>
          <p className="text-muted-foreground">
            Sie haben einen Fehler entdeckt, eine veraltete Kondition gefunden oder eine Frage
            zu einer Karte? Schreiben Sie uns an{" "}
            <a
              href="mailto:kontakt@kreditkarten.de"
              className="text-primary underline underline-offset-4"
            >
              kontakt@kreditkarten.de
            </a>
            . Wir prüfen jeden Hinweis und korrigieren belegte Fehler zeitnah.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Wichtiger Hinweis</h2>
          <p className="text-muted-foreground">
            Kreditkarten.de ist kein Kreditinstitut und bietet keine Finanz-, Rechts- oder
            Steuerberatung an. Maßgeblich sind stets die Vertragsbedingungen des jeweiligen
            Kartenherausgebers.
          </p>
        </section>
      </main>

      <PopularCardsSection
        currentPage="ueber-uns"
        forceVisible
        onViewDetails={(slug) => navigate(`/kreditkarten/${slug}`)}
        onViewAllCards={() => navigate("/kreditkarten")}
      />
      <Footer onNavigate={handleNavigate} />
    </div>
  );
}
